import constate from 'constate'
import { useCallback, useMemo } from 'react'
import { useFiltersState } from './state'
import { getFilterType } from './utils'

/**
 * Context provider for filters management. Any components rendered inside
 * `FiltersProvider` have access to a number of filters-related hooks that
 * allow easy access to and management of filters, the values for each filter,
 * the combined URL search params for the filter values, and access to the
 * `onApply` and `onClear` callbacks.
 *
 * See `useFiltersState` for more information.
 *
 * @typedef FiltersProvider
 * @deprecated Use ListLayout instead which is a higher-level component that configure a whole list page for you.
 * @see ListLayout
 */
/**
 * Provides access to all state in the surrounding filters context.
 *
 * Note that this hook will update all consumers anytime any piece of the
 * filters context state changes. For more stable selectors, use `useFilters`,
 * `useFilterValues`, and `useFilterParams` instead.
 *
 * @typedef useFiltersContext
 */
/**
 * Provides access to the filters config in the surrounding filters context. This
 * config is an array of filter configuration objects, which specify the type
 * and options for each filter.
 *
 * @typedef useFilters
 */
/**
 * Provides access to the values of each filter in the surrounding filters
 * context.
 *
 * @typedef useFilterValues
 */
/**
 * Provides access to the URL search params for each filter. These params can
 * be used directly with an API call, or converted into a querystring and used to
 * update the URL.
 *
 * @typedef useFilterParams
 */
/**
 * Provides access to the filter value updater function. This function takes an
 * `id` and a `value`, and updates the value of the filter with that specific
 * id.
 *
 * @typedef useFilterUpdater
 */

export const [
  FiltersProvider,
  useFiltersContext,
  useFilters,
  useFilterValues,
  useFilterParams,
  useFilterUpdater,
  useFiltersClear,
  useRestoreSavedFilters,
  useFiltersInitializing
] = constate(
  useFiltersState,
  value => value,
  value => value.filters,
  value => value.values,
  value => value.params,
  value => value.updateFilterValue,
  value => value.clear,
  value => value.restoreSavedFilter,
  value => value.areFiltersInitializing
)

/**
 * Provides the configuration, filter type, value, and onChange handler for a
 * specific filter.
 *
 * @param {string} id The id of the filter to select.
 */
export const useFilter = id => {
  const filters = useFilters()
  const values = useFilterValues()
  const updateFilterValue = useFilterUpdater()
  const value = values[id]
  const filter = useMemo(() => filters.find(filter => filter.id === id), [id, filters])
  const onChange = useCallback(value => updateFilterValue(id, value), [updateFilterValue, id])

  // memoize the results
  return useMemo(() => ({ filter, value, onChange, type: getFilterType(filter) }), [filter, value, onChange])
}
